import { memo, useMemo } from "react";
import PropTypes from "prop-types";

import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";

import { PaginationAction, Loader } from "components";
import { SoTrackingTableRow } from "../components";

import { SO_TABLE_COLUMNS } from "../constants";

const SoTrackingTable = ({
  orders,
  pagination,
  onPageChange,
  onRowsPerPageChange,
  expandedRows,
  onToggleExpand,
  isLoading,
  selectedFilters,
  filters,
}) => {
  const theme = useTheme();
  const expandedRowIds = useMemo(() => new Set(expandedRows), [expandedRows]);

  return (
    <Box>
      <TableContainer
        component={Paper}
        sx={{
          width: "100%",
          maxWidth: "100%",
          overflowX: "auto",
          boxShadow: 0,
          border: "1px solid",
          borderColor: "grey.300",
        }}
      >
        <Table>
          <TableHead>
            <TableRow>
              {SO_TABLE_COLUMNS.map((column) => (
                <TableCell key={column}>
                  <Typography
                    variant="caption"
                    sx={{ color: theme.palette.text.primary, fontWeight: 700 }}
                  >
                    {column}
                  </Typography>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={SO_TABLE_COLUMNS.length}>
                  <Box
                    sx={{
                      width: "100%",
                      minHeight: "30vh",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      marginBottom: 15,
                    }}
                  >
                    <Loader />
                  </Box>
                </TableCell>
              </TableRow>
            ) : orders?.length > 0 ? (
              orders.map((order) => (
                <SoTrackingTableRow
                  order={order}
                  isExpanded={expandedRowIds.has(order.orderId)}
                  onToggleExpand={onToggleExpand}
                  columnLength={SO_TABLE_COLUMNS.length}
                  activeTab={selectedFilters} 
                />
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={SO_TABLE_COLUMNS.length}
                  align="center"
                  sx={{ py: 6 }}
                >
                  <Typography color="text.secondary">
                    No orders match the selected filters
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TablePagination
                colSpan={SO_TABLE_COLUMNS.length}
                count={pagination.totalCount}
                page={pagination.page}
                rowsPerPage={pagination.rowsPerPage}
                rowsPerPageOptions={[10, 20]}
                onPageChange={onPageChange}
                onRowsPerPageChange={onRowsPerPageChange}
                ActionsComponent={PaginationAction}
                sx={{ borderBottom: 0 }}
              />
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>
    </Box>
  );
};

SoTrackingTable.propTypes = {
  orders: PropTypes.arrayOf(PropTypes.object).isRequired,
  pagination: PropTypes.shape({
    page: PropTypes.number.isRequired,
    rowsPerPage: PropTypes.number.isRequired,
    totalCount: PropTypes.number.isRequired,
  }).isRequired,
  onPageChange: PropTypes.func.isRequired,
  onRowsPerPageChange: PropTypes.func.isRequired,
  expandedRows: PropTypes.arrayOf(PropTypes.string).isRequired,
  onToggleExpand: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
};

export default memo(SoTrackingTable);
